/**
 * Training SOP — Estimator track.
 *
 * Short by design. An estimator's day happens mostly in Bid Tracker and Sage
 * Intacct; the Project Tracker matters at three moments — when a bid becomes a
 * job, when the real job number exists, and when the job is handed to the PM.
 * The middle one is the important one: nothing on the job works properly until
 * the estimator enters the Sage number.
 *
 * NOTE ON ROLE DETECTION: there IS an Estimator role now, added deliberately on
 * 2026-08-03 (see db/migrations/2026-08-03_estimator-role.sql) because estimators
 * were indistinguishable in RBAC — they held ProjectEditor like PMs, and "who is
 * an estimator" was only knowable from projects.estimator_id. This comment used
 * to say the opposite ("don't fix that by inventing a role"), which was right
 * while the role didn't exist: badging people into a track by guessing at
 * permissions is what made an admin read as a Project Manager.
 *
 * ⚠️ The badge only appears for someone HOLDING the role. Today's estimators
 * hold ProjectEditor, so until an admin assigns it nobody is auto-selected into this
 * track — they can still pick it, exactly as before.
 *
 * Verified against the code (2026-07-25 sweep) and the product owner's ownership answers.
 */
import { OWNERSHIP, type Track } from '../types';

export const ESTIMATOR_TRACK: Track = {
  id: 'estimator',
  label: 'Estimator',
  audience:
    'Estimators. Short — three moments matter, and one of them blocks the whole job until you act.',
  suggestedFor: { anyRole: ['Estimator'] },
  sections: [
    // ── 1. From bid to job ──────────────────────────────────────────────────
    {
      id: 'est-bid-to-job',
      title: 'How your bids turn into jobs',
      intro:
        'You never create a project by hand. Bid Tracker does it for you, in two steps.',
      topics: [
        {
          id: 'est-lifecycle',
          title: 'What Bid Tracker creates, and when',
          summary:
            'When a bid reaches "chance", a placeholder job appears here automatically. When you win it, that placeholder unlocks and renames itself. Nothing here needs doing — it just helps to know what you are looking at.',
          table: {
            caption: 'The two automatic steps',
            headers: ['When', 'What appears here', 'State'],
            rows: [
              ['Bid marked a chance', 'A job numbered SYSTAC-###', 'Read-only. Nobody can edit it.'],
              ['Bid marked Closed Won', 'Renames itself to ClosedWon-###', 'Editable, but still no equipment, RFIs, submittals or daily logs.'],
              ['You enter the Sage number', 'The real job number', 'Fully open.'],
            ],
          },
          watchOut: [
            'A bid marked Closed Lost, Closed Other or Cancelled archives its placeholder automatically — unless someone has already given it a real job number, in which case it is treated as a live job and left alone.',
          ],
          deepLink: '/projects',
        },
        {
          id: 'est-find-your-jobs',
          title: 'Finding your jobs',
          summary:
            'You are attached to every job where you are named as the estimator, so they appear under My Projects without you doing anything.',
          steps: [
            'Open **Projects**.',
            'Leave the toggle on **My Projects** to see only jobs you are the estimator on.',
            'Switch to **All** if you need to look beyond your own.',
          ],
          deepLink: '/projects',
        },
        {
          id: 'est-systac-forecast',
          title: 'Telling the schedulers a SYSTAC forecast has moved',
          // Facts verified against manpower-requests.service.newContext /
          // canRequest (2026-09-02): on a bid the owner is projects.estimator_id;
          // the form pre-fills bid_peak_crew and the current-or-original start
          // plus duration (inclusive, the grid's rule); nothing writes Bid
          // Tracker or bid_peak_crew.
          summary:
            'The crew you forecast at bid already shows on the company-wide schedule as a SYSTAC row. When that forecast changes before award — scope, schedule, the owner — send a **Forecast Update** from the bid\'s **Manpower** tab instead of an email. It goes to the schedulers, and it is logged on the job.',
          steps: [
            'Open the SYSTAC job\'s **Manpower** tab and press **+ Forecast Update**. It is there if you are the bid\'s estimator — or you hold the manpower request permission, or you are an admin.',
            'The form opens with the **peak crew** and **anticipated start and finish** the Tracker already holds from Bid Tracker. Change what has moved.',
            'Under **What changed**, say what moved and why — this is what the schedulers read.',
            '**Send update**.',
          ],
          watchOut: [
            'This tells people; it does not change the forecast itself. The SYSTAC row on the schedule still comes from Bid Tracker — update the bid there as you always have.',
            'The schedulers may book your update as **anticipated** crew so the schedule shows what could be coming. That is their act, not something the update does.',
            'Once the bid is won, the button becomes the PM\'s **Manpower Request** and the forecast door closes — crew on a won job is a request, not a forecast.',
          ],
          owner: OWNERSHIP.manpowerRequests,
          deepLinkNote: 'the bid’s Manpower tab, top card',
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 2. The job number — the one that matters ────────────────────────────
    {
      id: 'est-job-number',
      title: 'Entering the real job number',
      intro:
        'This is the task that belongs to you and to nobody else, and the one people will chase you for.',
      topics: [
        {
          id: 'est-enter-job-number',
          title: 'Replacing the placeholder with the Sage number',
          summary:
            'You set the job up in Sage Intacct, so you are the one holding its real number. Entering it here is what turns a placeholder into a working job — equipment requests, RFIs, submittals, daily logs and materials all stay locked until you do.',
          owner: OWNERSHIP.jobNumber,
          steps: [
            'Set the job up in **Sage Intacct** as usual — that is where the number comes from.',
            'In the Project Tracker, open the job and go to **Overview ▸ Basics** → **Edit**.',
            'Replace the placeholder in **Job #** with the Sage number.',
            'Save. The amber lock banner and the RENUMBER pill both clear, for everyone.',
          ],
          watchOut: [
            'The amber **RENUMBER** pill on the Projects list is your to-do list. Any job showing it is waiting on you, and quite possibly has a crew waiting too — this is the single most common reason field staff are blocked.',
            'Once a job carries a real number it is **adopted**: Bid Tracker no longer controls its lifecycle, so a later status change over there will not archive it.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 3. Handing over ─────────────────────────────────────────────────────
    {
      id: 'est-handoff',
      title: 'Handing the job to the project team',
      intro:
        'What you record at handoff is what the PM plans from — and what gets compared against reality at closeout.',
      topics: [
        {
          id: 'est-bid-handoff',
          title: 'Bid & Handoff figures',
          summary:
            'The bid assumptions the project team will plan against: when you handed off, and the crew and hours the job was priced on.',
          steps: [
            'Open the job and go to **Overview ▸ Bid & Handoff** → **Edit**.',
            'Set **Estimator Handoff** — the date you handed the job over.',
            'Enter **Peak Crew (Bid)** and **Bid Weekly Hours** — the manning the price assumed.',
            'Save.',
          ],
          watchOut: [
            'Start and completion dates are **not** here. They live on the **Schedule & Dates** card and belong to the PM — this section is only what the bid assumed.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'est-notes',
          title: 'Leaving notes for the project team',
          summary:
            'Estimator Notes is a dedicated place to record the assumptions, exclusions and worries that never fit in a number. It is the cheapest thing you can do to prevent a surprise later.',
          steps: [
            'On **Overview**, find the notes area and choose **Estimator Notes**.',
            'Add a note. It saves immediately — there is no separate Save on the section.',
          ],
          watchOut: [
            'Notes are separated into Estimator, PM and general so nobody overwrites anyone. Use the Estimator category rather than the general one, so the PM knows the note carries bid intent.',
            'You can edit and delete your own notes. Older imported notes with no recorded author are read-only.',
          ],
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'est-projected-hours',
          title: 'Entering the estimated labor hours',
          summary:
            'Every job needs the estimated labor hours typed in, by you, at set-up. This is the target every hour the crew burns gets measured against — until it is entered, the job carries an **Estimated labor hours missing** flag on its Overview.',
          steps: [
            'Open the job\'s **Manpower** tab.',
            'Find **Projected hours** at the top and press **Enter hours**.',
            'Type the labor hours you priced the job at, and Save.',
            'Do this when the job is set up, alongside entering the Sage job number — not months later.',
          ],
          watchOut: [
            '**This used to come from Intacct automatically. It no longer does** (changed August 2026). Intacct **autocalculated** the figure and it was routinely lower than the real labor, so the app was showing a number nobody stood behind.',
            'Where a job still shows a grayed **"For reference, Intacct calculated…"** line, treat it as a hint and nothing more. It is frozen, unverified, and usually low — the number that counts is the one you type.',
            'Clearing the box and saving takes the hours back off the job, which puts the flag back. Nothing else fills it in for you.',
          ],
          owner: OWNERSHIP.laborHours,
          deepLinkNote: 'the job’s Manpower tab, top card',
          access: { anyPermission: ['projects.edit'] },
        },
        {
          id: 'est-concrete-bpo',
          title: 'The concrete supplier and blanket PO',
          summary:
            'You raise the blanket PO, so you are the one holding its number. Recording it on the job is what lets the office order concrete without ringing round to find it. The supplier goes here too, on the jobs where you are the one who lined it up.',
          steps: [
            'Open the job\'s **QA / QC** tab and find **Concrete Ordering**.',
            'Set the **concrete** and **aggregate** blanket POs — only the ones the job actually draws against. A job that buys no aggregate leaves that field blank.',
            'Set the **supplier** as well if you had one lined up at bid. On jobs where the PM buys the concrete out, leave it to them rather than guessing.',
            'Save. They then show on every pour card for the job and beside it on the company-wide pour schedule.',
          ],
          watchOut: [
            'While nothing at all is recorded, the pour schedule reads **"No supplier / BPO set"** against the job and whoever is ordering has to come and find you. **It is not a completeness check** — that warning only shows while the setup is entirely empty, so a supplier saved on its own clears it even though a PO the job needs is still missing.',
            'Type the PO exactly as it reads on the document. The tracker does not look it up in Sage — it exists to be read out on the phone.',
          ],
          owner: OWNERSHIP.concreteSupplierAndBpo,
          deepLinkNote: 'Open a job from Projects, then the QA / QC tab.',
          access: { anyPermission: ['projects.edit'] },
        },
      ],
    },

    // ── 4. Watching the job against your estimate ───────────────────────────
    // Gated the way the PM track's reports section is, and for the same reason:
    // reports.view opens the tab, each report has a permission of its own, and
    // neither consults the company cost setting (ProjectReportsTab, issue #347).
    // The Man Hours topic leans on est-projected-hours above — the typed figure
    // the report sets beside its derived total is the estimator's own.
    // Verified against JobCostReport / ManHoursReport (2026-10-05).
    {
      id: 'est-reports',
      title: 'Watching the job against your estimate',
      intro:
        'Two reports on each job you can open set what you priced against what the job is costing — one in dollars, one in hours — so you can see how a job is running against your price while it is still running.',
      access: { anyPermission: ['reports.view'], hideFromFieldLeaderOnly: true },
      topics: [
        {
          id: 'est-job-cost-report',
          title: 'The Job Cost Management Report',
          summary:
            'Your estimate by cost code against what the job has actually cost, split into labor, material, equipment, subs and other. It is where you see, line by line, whether the price is holding.',
          steps: [
            'Open the job from **Projects** and go to its **Reports** tab. If you hold both reports you get a list — choose **Job Cost Management Report**.',
            'Read **Estimated**, **Actual** and **Balance** by cost group. **Balance** is Estimated − Actual, so a negative balance, shown in red, is over what was priced.',
            'Work from the **Total** band down to the codes that moved. Change orders sit in a block of their own, apart from the original codes.',
            'Turn on **Show open commitment and exposure** to add what is committed on POs and subcontracts, and what is left once both actual and committed are counted.',
            'Use **Export to Excel** to take the figures away.',
          ],
          watchOut: [
            'Estimated is the Intacct job estimate as last synced, with any pending change scope included and called out in a note above the table. The notes also say when no estimate has synced for the job — then every Estimated figure is $0, which is missing data, not a zero budget.',
            '**Exposure is not a forecast.** It counts nothing still to be bought, so it reads optimistically mid-buyout.',
          ],
          deepLinkNote: 'Open a job from Projects, then the Reports tab in the left-hand navigation.',
          access: { anyPermission: ['reports.job_cost'] },
        },
        {
          id: 'est-man-hours-report',
          title: 'The Man Hours Report',
          summary:
            'Estimated man hours by cost code against actual Paycor hours. The estimated hours are worked out from your labor estimate, so they are only as good as the labor dollars you priced.',
          steps: [
            'On the job\'s **Reports** tab, choose **Man Hours Report**.',
            'Note **Rate used** in the header. Each code\'s estimated hours are its estimated labor dollars divided by that loaded rate per man hour — the rate estimators price labor at, which an administrator sets rather than anyone per job.',
            'Read **Estimated MH**, **Actual MH** and **Balance** by code; a negative balance is over the estimate.',
            'Check **Estimator\'s job total** — the estimated labor hours you typed at job set-up, covered under **Entering the estimated labor hours** — against the report\'s own total. If it reads **none on file**, those hours have not been entered yet.',
          ],
          watchOut: [
            '**The two figures answer the same question and should roughly agree.** The report\'s hours come from the labor estimate and the company rate; the typed hours are your own number. If the report\'s total is more than half above your figure, or more than a third below it, the report flags it, and one of them is likely wrong — check the typed figure and the labor estimate before anyone manages the job to either.',
            'Actual hours come from Paycor timecards and reach back only as far as the dates stated under the header. On a job that started earlier the actuals are incomplete, and the report says so.',
          ],
          deepLinkNote: 'Open a job from Projects, then the Reports tab in the left-hand navigation.',
          access: { anyPermission: ['reports.man_hours'] },
        },
      ],
    },

    // ── 5. After the job ────────────────────────────────────────────────────
    {
      id: 'est-closeout',
      title: 'After the job finishes',
      topics: [
        {
          id: 'est-ppr',
          title: 'The Post Project Review survey',
          summary:
            'When a job closes out, the PPR coordinator collects feedback from everyone who touched it — including an Estimator survey asking how the job actually went against how it was priced. You will get it by email.',
          steps: [
            'Watch for the PPR request email from the coordinator.',
            'Fill in the linked form.',
            'That is it — your response is recorded automatically when you submit.',
          ],
          watchOut: [
            'This is where the estimating feedback loop actually closes. A job that ran badly against its bid only teaches the next bid something if the survey gets answered.',
          ],
        },
      ],
    },
  ],
};
